import { readClientPageviewBody } from './client.js';
import { sendIdempotentTrackingPayload } from './ingest.js';
export const CLIENT_EVENT_ROUTE = '/api/event';
const PROMO_CHOICES = ['scams', 'identity', 'address', 'stalkers', 'data-sale', 'family'];
export const PROMO_QUIZ_STEP_KINDS = { concern: 'question', approach: 'story', claim: 'form' };
export const PROMO_QUIZ_FLOW_VERSION = 'promo_quiz_v1';
const IDENTIFIER = /^[a-z][a-z0-9_-]{0,63}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const COMMON_KEYS = ['funnel_id', 'flow_version', 'journey_id', 'sequence', 'experiment_id', 'variant_id', 'expected_share', 'concern'];
const STEP_KEYS = [...COMMON_KEYS, 'step_id', 'step_kind'];
const PROMO_COMMON_KEYS = ['funnel_id', 'flow_version', 'journey_id', 'sequence', 'concern'];
const PROMO_STEP_KEYS = [...PROMO_COMMON_KEYS, 'step_id', 'step_kind'];
const CONCERNS = ['spam', 'address', 'identity', 'family', 'privacy'];
const STEP_KINDS = ['question', 'story', 'form', 'verification', 'offer', 'transition'];
const ERROR_CODES = new Set([
    'validation_failed', 'invalid_pin', 'authenticator_required', 'invalid_authenticator', 'authenticator_unavailable',
    'too_many_attempts', 'sso_required', 'sso_not_configured', 'additional_sign_in_required', 'unsupported_origin',
    'minor_account', 'service_account', 'cross_site_request', 'member_sign_in_required', 'sign_in_required',
    'session_changed', 'cookies_blocked', 'rate_limited', 'invalid_response', 'request_failed', 'network_error',
    'timeout', 'pricing_unavailable', 'unexpected',
]);
/** Clusterfuck persists saved Funnel identifiers as a prefix and 96-bit hex ID. */
export function isSavedFunnelId(value) {
    return typeof value === 'string' && value.length === 28 && /^fun_[0-9a-f]{24}$/.test(value);
}
function record(raw) {
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw);
}
function keysAre(raw, allowed) {
    return Object.keys(raw).every((key) => allowed.includes(key));
}
function identifier(raw) {
    return typeof raw === 'string' && IDENTIFIER.test(raw);
}
/** Event properties describe fixed UI choices; arbitrary metadata and personal values are rejected. */
export function readClientEventBody(raw, now = Date.now()) {
    if (!record(raw) || !keysAre(raw, ['name', 'properties', 'idempotencyKey', 'path', 'ts']))
        return null;
    if (typeof raw.idempotencyKey !== 'string' || !UUID.test(raw.idempotencyKey))
        return null;
    if (typeof raw.ts !== 'string')
        return null;
    const capturedAt = Date.parse(raw.ts);
    if (!Number.isFinite(capturedAt) || new Date(capturedAt).toISOString() !== raw.ts
        || capturedAt < now - 72 * 60 * 60 * 1000 || capturedAt > now + 5 * 60 * 1000)
        return null;
    const page = readClientPageviewBody({ path: raw.path, referrerPath: null });
    if (!page || !record(raw.properties))
        return null;
    const common = { idempotencyKey: raw.idempotencyKey, path: page.path, ts: raw.ts };
    const definition = raw.properties.funnel_id === 'privacy_quiz' ? readExploreEvent(raw.name, raw.properties)
        : raw.properties.funnel_id === 'promo_quiz' ? readPromoEvent(raw.name, raw.properties) : null;
    return definition ? { ...common, ...definition } : null;
}
function readExploreEvent(name, properties) {
    const journey = readJourney(properties);
    if (!journey)
        return null;
    if (name === 'quiz_started' || name === 'experiment_exposed') {
        return keysAre(properties, COMMON_KEYS) ? { name, properties: journey } : null;
    }
    if (!identifier(properties.step_id))
        return null;
    const kind = STEP_KINDS.find((value) => value === properties.step_kind);
    if (properties.step_kind !== undefined && kind === undefined)
        return null;
    const step = { ...journey, step_id: properties.step_id, ...(kind ? { step_kind: kind } : {}) };
    if (name === 'quiz_step_viewed' || name === 'quiz_back' || name === 'quiz_dismissed') {
        return keysAre(properties, STEP_KEYS) ? { name, properties: step } : null;
    }
    if (name === 'quiz_step_completed') {
        if (!keysAre(properties, [...STEP_KEYS, 'duration_ms']))
            return null;
        const duration = properties.duration_ms;
        if (!validDuration(duration))
            return null;
        return { name, properties: { ...step, ...(duration === undefined ? {} : { duration_ms: duration }) } };
    }
    if (name === 'quiz_step_error') {
        if (!keysAre(properties, [...STEP_KEYS, 'error_code']) || typeof properties.error_code !== 'string' || !ERROR_CODES.has(properties.error_code))
            return null;
        return { name, properties: { ...step, error_code: properties.error_code } };
    }
    if (name === 'quiz_choice_selected') {
        if (!keysAre(properties, [...STEP_KEYS, 'choice_id', 'operation']))
            return null;
        if (!identifier(properties.choice_id))
            return null;
        if (properties.operation !== undefined && properties.operation !== 'select' && properties.operation !== 'deselect')
            return null;
        return {
            name,
            properties: {
                ...step,
                choice_id: properties.choice_id,
                ...(properties.operation === undefined ? {} : { operation: properties.operation }),
            },
        };
    }
    if (name === 'plan_selected') {
        if (!keysAre(properties, [...STEP_KEYS, 'tier_id']))
            return null;
        if (properties.tier_id !== 'essentials' && properties.tier_id !== 'pro' && properties.tier_id !== 'signature')
            return null;
        return { name, properties: { ...step, tier_id: properties.tier_id } };
    }
    return null;
}
function readJourney(properties) {
    const identity = readJourneyIdentity(properties);
    if (!identifier(properties.flow_version) || !identifier(properties.experiment_id)
        || !identity
        || (properties.variant_id !== 'control' && properties.variant_id !== 'clustered') || properties.expected_share !== 0.5)
        return null;
    const concern = CONCERNS.find((value) => value === properties.concern);
    if (properties.concern !== undefined && concern === undefined)
        return null;
    return {
        funnel_id: 'privacy_quiz', flow_version: properties.flow_version,
        ...identity,
        experiment_id: properties.experiment_id, variant_id: properties.variant_id,
        expected_share: 0.5, ...(concern ? { concern } : {}),
    };
}
function readJourneyIdentity(properties) {
    if (typeof properties.journey_id !== 'string' || !UUID.test(properties.journey_id)
        || typeof properties.sequence !== 'number' || !Number.isSafeInteger(properties.sequence)
        || properties.sequence < 1 || properties.sequence > 1_000_000)
        return null;
    return { journey_id: properties.journey_id, sequence: properties.sequence };
}
function validDuration(value) {
    return value === undefined || (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= 24 * 60 * 60 * 1000);
}
function readPromoEvent(name, properties) {
    const identity = readJourneyIdentity(properties);
    if (!identity || properties.flow_version !== PROMO_QUIZ_FLOW_VERSION)
        return null;
    const concern = PROMO_CHOICES.find((choice) => choice === properties.concern);
    if (properties.concern !== undefined && concern === undefined)
        return null;
    const journey = { ...identity, funnel_id: 'promo_quiz', flow_version: PROMO_QUIZ_FLOW_VERSION, ...(concern ? { concern } : {}) };
    if (name === 'quiz_started') {
        const trigger = properties.trigger;
        if (!keysAre(properties, [...PROMO_COMMON_KEYS, 'trigger']) || (trigger !== 'forced' && trigger !== 'delay' && trigger !== 'exit_intent'))
            return null;
        return { name, properties: { ...journey, trigger } };
    }
    const step = readPromoStep(properties, journey);
    if (!step)
        return null;
    if (name === 'quiz_step_viewed' || name === 'quiz_dismissed') {
        return keysAre(properties, PROMO_STEP_KEYS) ? { name, properties: step } : null;
    }
    if (name === 'quiz_step_completed') {
        const duration = properties.duration_ms;
        if (!keysAre(properties, [...PROMO_STEP_KEYS, 'duration_ms']) || !validDuration(duration))
            return null;
        return { name, properties: { ...step, ...(duration === undefined ? {} : { duration_ms: duration }) } };
    }
    if (name === 'quiz_choice_selected') {
        const choice = PROMO_CHOICES.find((value) => value === properties.choice_id);
        if (!keysAre(properties, [...PROMO_STEP_KEYS, 'choice_id', 'operation']) || step.step_id !== 'concern'
            || !choice || properties.operation !== 'select')
            return null;
        return { name, properties: { ...step, choice_id: choice, operation: 'select' } };
    }
    if (name === 'quiz_step_error') {
        if (!keysAre(properties, [...PROMO_STEP_KEYS, 'error_code']) || step.step_id !== 'claim' || properties.error_code !== 'validation_failed')
            return null;
        return { name, properties: { ...step, error_code: 'validation_failed' } };
    }
    if (name === 'quiz_back' && step.step_id === 'approach' && keysAre(properties, PROMO_STEP_KEYS)) {
        return { name, properties: step };
    }
    return null;
}
function readPromoStep(properties, journey) {
    switch (properties.step_id) {
        case 'concern': return properties.step_kind === PROMO_QUIZ_STEP_KINDS.concern
            ? { ...journey, step_id: 'concern', step_kind: PROMO_QUIZ_STEP_KINDS.concern } : null;
        case 'approach': return properties.step_kind === PROMO_QUIZ_STEP_KINDS.approach
            ? { ...journey, step_id: 'approach', step_kind: PROMO_QUIZ_STEP_KINDS.approach } : null;
        case 'claim': return properties.step_kind === PROMO_QUIZ_STEP_KINDS.claim
            ? { ...journey, step_id: 'claim', step_kind: PROMO_QUIZ_STEP_KINDS.claim } : null;
        default: return null;
    }
}
export async function sendEvent(config, payload) {
    const response = await sendIdempotentTrackingPayload(config, JSON.stringify(payload));
    if (!response)
        return 'failed';
    try {
        const receipt = await response.json();
        if (!record(receipt) || receipt.success !== true || !record(receipt.data))
            return 'failed';
        if (receipt.data.bot === true)
            return 'suppressed';
        return typeof receipt.data.eventId === 'string' && receipt.data.eventId.length > 0
            && typeof receipt.data.duplicate === 'boolean' ? 'accepted' : 'failed';
    }
    catch {
        console.error(JSON.stringify({ event: 'tracking_ingest_failed', reason: 'invalid_receipt' }));
        return 'failed';
    }
}
